import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { usePhase } from '../../../contexts/PhaseContext';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  TextField as SearchField,
  Tabs,
  Tab
} from '@mui/material';
import {
  Subject as SubjectIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Search as SearchIcon,
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Category as CategoryIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { fetchJson } from '../../../utils/fetchJson';
import AppLoader from '../../../components/AppLoader';
import useScopeAutoRefresh from '../../../hooks/useScopeAutoRefresh';

const ManageSubjectsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { activePhase } = usePhase();
  const resolvedPhaseCode = useMemo(() => {
    const fromCtx = activePhase || localStorage.getItem('schoolAdmin_activePhase') || localStorage.getItem('activePhase');
    return fromCtx ? String(fromCtx).toLowerCase() : '';
  }, [activePhase]);
  const resolvedPhaseId = useMemo(() => {
    const rawPhaseId = localStorage.getItem('schoolAdmin_activePhaseId') || localStorage.getItem('activePhaseId') || '';
    const parsedPhaseId = parseInt(String(rawPhaseId), 10);
    if (Number.isInteger(parsedPhaseId)) return parsedPhaseId;
    if (resolvedPhaseCode === 'sec' || resolvedPhaseCode === 'secondary') return 2;
    if (resolvedPhaseCode === 'nur_pri' || resolvedPhaseCode === 'nursery' || resolvedPhaseCode === 'nursery_primary') return 1;
    return null;
  }, [resolvedPhaseCode]);
  const filterByPhaseContext = useCallback((items = []) => {
    if (!Array.isArray(items)) return [];
    if (!resolvedPhaseId) return items;
    const nurPriLevels = ['nursery', 'primary'];
    const secLevels = ['first_cycle', 'second_cycle'];
    const forbiddenLevels = resolvedPhaseCode === 'nur_pri' ? secLevels
      : resolvedPhaseCode === 'sec' ? nurPriLevels : [];
    return items.filter((item) => {
      // Level-based guard: reject subjects whose level belongs to the other phase
      const lvl = (item?.level || '').toString().trim().toLowerCase();
      if (lvl && forbiddenLevels.includes(lvl)) return false;
      const candidateId = Number(item?.phaseId ?? item?.PhaseId ?? item?.phase_id);
      if (Number.isInteger(candidateId)) return candidateId === resolvedPhaseId;
      const candidateCode = (item?.phaseCode ?? item?.Phase?.code ?? item?.phase_code ?? '').toString().toLowerCase();
      if (candidateCode) return candidateCode === resolvedPhaseCode;
      return false;
    });
  }, [resolvedPhaseCode, resolvedPhaseId]);
  const coefficientRules = useMemo(() => {
    if (resolvedPhaseCode === 'sec') {
      return {
        title: t('manageSubjects.coefficientRules.secondary.title'),
        helper: t('manageSubjects.coefficientRules.secondary.helper'),
        bullets: [
          t('manageSubjects.coefficientRules.secondary.bullets.firstCycle'),
          t('manageSubjects.coefficientRules.secondary.bullets.secondCycle')
        ]
      };
    }
    if (resolvedPhaseCode === 'nur_pri') {
      return {
        title: t('manageSubjects.coefficientRules.nurseryPrimary.title'),
        helper: t('manageSubjects.coefficientRules.nurseryPrimary.helper'),
        bullets: [
          t('manageSubjects.coefficientRules.nurseryPrimary.bullets.nursery'),
          t('manageSubjects.coefficientRules.nurseryPrimary.bullets.primary')
        ]
      };
    }
    return {
      title: t('manageSubjects.coefficientRules.default.title'),
      helper: t('manageSubjects.coefficientRules.default.helper'),
      bullets: [t('manageSubjects.coefficientRules.default.bullets.autoOverrides')]
    };
  }, [resolvedPhaseCode, t]);
  
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [subjects, setSubjects] = useState([]);
  const [categories, setCategories] = useState([]);
  const [classes, setClasses] = useState([]);
  const [subjectClassesMap, setSubjectClassesMap] = useState({});
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [levelFilter, setLevelFilter] = useState('all');
  const [classFilter, setClassFilter] = useState('all');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingSubject, setEditingSubject] = useState(null);
  const [editFormData, setEditFormData] = useState({
    name: '',
    code: '',
    description: '',
    categoryId: '',
    coefficient: ''
  });

  useEffect(() => {
    fetchData();
  }, [activePhase]);

  const levelLabel = useCallback((lvl) => {
    const v = (lvl || '').toString();
    if (v === 'nursery') return t('subjectManagement.add.levels.nursery', { defaultValue: 'Nursery' });
    if (v === 'primary') return t('subjectManagement.add.levels.primary', { defaultValue: 'Primary' });
    if (v === 'first_cycle') return t('subjectManagement.add.levels.firstCycle', { defaultValue: 'First Cycle' });
    if (v === 'second_cycle') return t('subjectManagement.add.levels.secondCycle', { defaultValue: 'Second Cycle' });
    return v || t('common.na', { defaultValue: 'N/A' });
  }, [t]);

  const getSubjectDisplayLevel = useCallback((subject) => {
    if (!subject) return null;
    if (subject.level) return subject.level;
    const classesForSubject = subjectClassesMap?.[String(subject.id)] || subjectClassesMap?.[subject.id] || [];
    const levels = Array.from(new Set((classesForSubject || []).map(c => c?.level).filter(Boolean)));
    if (levels.length === 1) return levels[0];
    return null;
  }, [subjectClassesMap]);

  const getCategoryDisplayLevel = useCallback((cat) => {
    if (!cat) return null;
    if (cat.level) return cat.level;
    const levels = Array.from(new Set(
      (subjects || [])
        .filter(s => String(s?.categoryId) === String(cat.id))
        .map(s => getSubjectDisplayLevel(s) || s?.level)
        .filter(Boolean)
    ));
    if (levels.length === 1) return levels[0];
    return null;
  }, [getSubjectDisplayLevel, subjects]);

  const fetchData = async () => {
    let maxGuard;
    try {
      setError(null);
      setLoading(true);
      maxGuard = setTimeout(() => setLoading(false), 2000);
      // Phase-scoped no-flash cache keys
      const schoolId = localStorage.getItem('schoolId') || localStorage.getItem('activeSchoolId') || localStorage.getItem('scopeSchoolId') || '';
      const phaseId = localStorage.getItem('schoolAdmin_activePhaseId') || localStorage.getItem('activePhaseId') || '';
      const phaseCode = (activePhase || localStorage.getItem('schoolAdmin_activePhase') || localStorage.getItem('activePhase') || '');
      const ctxKey = `${schoolId}|${phaseId || phaseCode || '-'}`;
      const cacheKeySubjects = `manageSubjects:subjects:${ctxKey}`;
      const cacheKeyCategories = `manageSubjects:categories:${ctxKey}`;
      // Prefill from cache
      let prefilled = false;
      try { const raw = localStorage.getItem(cacheKeySubjects); if (raw) { const cached = JSON.parse(raw); if (Array.isArray(cached)) { setSubjects(cached); if (cached.length) prefilled = true; } } } catch(_) {}
      try { const raw = localStorage.getItem(cacheKeyCategories); if (raw) { const cached = JSON.parse(raw); if (Array.isArray(cached)) { setCategories(cached); if (cached.length) prefilled = true; } } } catch(_) {}

      if (prefilled) setLoading(false);

      // Fetch subjects with their categories (context-aware)
      const ayKey = schoolId ? `${schoolId}:currentAcademicYearId` : 'currentAcademicYearId';
      const ayId = localStorage.getItem(ayKey) || '';
      const token = localStorage.getItem('token') || localStorage.getItem('authToken') || localStorage.getItem('accessToken') || '';
      const headers = schoolId 
        ? { 'X-Scope-School-Id': schoolId, 'X-Active-Phase': phaseCode, 'X-Active-Phase-Id': phaseId, ...(ayId ? { 'X-Academic-Year-Id': ayId } : {}), ...(token ? { 'Authorization': `Bearer ${token}` } : {}), 'Accept': 'application/json' }
        : { 'X-Active-Phase': phaseCode, 'X-Active-Phase-Id': phaseId, ...(ayId ? { 'X-Academic-Year-Id': ayId } : {}), ...(token ? { 'Authorization': `Bearer ${token}` } : {}), 'Accept': 'application/json' };
      const qsParts = [
        phaseCode ? `phase=${encodeURIComponent(phaseCode)}` : '',
        phaseId ? `phaseId=${encodeURIComponent(phaseId)}` : '',
        schoolId ? `schoolId=${encodeURIComponent(schoolId)}` : ''
      ].filter(Boolean);
      const qsTail = qsParts.join('&');
      const namesQsTail = [
        ...qsParts,
        ayId ? `academicYearId=${encodeURIComponent(ayId)}` : ''
      ].filter(Boolean).join('&');
      let subjectsData = await fetchJson(`/api/subjects?include=category${qsTail ? `&${qsTail}` : ''}`, { headers });
      let subjectsArray = Array.isArray(subjectsData) ? subjectsData : (subjectsData?.subjects || subjectsData?.data || []);
      if (!Array.isArray(subjectsArray) || subjectsArray.length === 0) {
        try {
          const scopedAlloc = await fetchJson(`/api/allocations/subjects${qsTail ? `?${qsTail}` : ''}`, { headers });
          subjectsArray = Array.isArray(scopedAlloc) ? scopedAlloc : (scopedAlloc?.subjects || scopedAlloc?.rows || scopedAlloc?.data || []);
        } catch (allocErr) {
          console.warn('[ManageSubjectsPage] Scoped subject fetch returned empty result', allocErr);
        }
      }
      subjectsArray = filterByPhaseContext(subjectsArray);
      setSubjects(subjectsArray);

      // Fetch categories
      const categoriesData = await fetchJson(`/api/subject-categories${qsTail ? `?${qsTail}` : ''}`, { headers });
      let categoriesArray = Array.isArray(categoriesData) ? categoriesData : 
                             (categoriesData?.categories || categoriesData?.data || []);
      categoriesArray = filterByPhaseContext(categoriesArray);
      setCategories(categoriesArray);

      const [classesAlloc, namesResp] = await Promise.all([
        fetchJson(`/api/allocations/classes${qsTail ? `?${qsTail}` : ''}`, { headers }).catch(() => []),
        fetchJson(`/api/allocations/class-subjects/names${namesQsTail ? `?${namesQsTail}` : ''}`, { headers }).catch(() => ({ map: {} }))
      ]);
      const classesArrayRaw = Array.isArray(classesAlloc) ? classesAlloc : (classesAlloc?.classes || classesAlloc?.rows || classesAlloc?.data || []);
      const classesArray = filterByPhaseContext(classesArrayRaw);
      setClasses(classesArray || []);
      const namesMap = namesResp && namesResp.map ? namesResp.map : {};
      const classLookup = {};
      (classesArray || []).forEach((c) => { if (c && c.id != null) classLookup[String(c.id)] = c; });
      const subjMap = {};
      Object.entries(namesMap || {}).forEach(([cid, subs]) => {
        const c = classLookup[String(cid)];
        if (!c) return;
        (Array.isArray(subs) ? subs : []).forEach((s) => {
          const sid = String(s?.id || '');
          if (!sid) return;
          if (!subjMap[sid]) subjMap[sid] = [];
          subjMap[sid].push({ id: c.id, name: c.name, level: c.level || null, section: c.section || null });
        });
      });
      setSubjectClassesMap(subjMap);

      // Update cache
      try { localStorage.setItem(cacheKeySubjects, JSON.stringify(subjectsArray)); } catch(_) {}
      try { localStorage.setItem(cacheKeyCategories, JSON.stringify(categoriesArray)); } catch(_) {}

    } catch (error) {
      console.error('Error fetching data:', error);
      setError(t('manageSubjects.errors.fetchData', { message: error.message }));
    } finally {
      setLoading(false);
      // clear guard timeout if still pending
      if (maxGuard) {
        try { clearTimeout(maxGuard); } catch(_) {}
      }
    }
  };

  useScopeAutoRefresh([fetchData]);

  const handleEditSubject = (subject) => {
    setEditingSubject(subject);
    const displayLevel = getSubjectDisplayLevel(subject);
    const variantLevel = subject?.level || displayLevel || '';
    setEditFormData({
      name: subject.name,
      code: subject.code,
      description: subject.description || '',
      level: displayLevel || '',
      categoryId: subject.categoryId || '',
      coefficient: subject.coefficient ?? '',
      coefficientFirstCycle: variantLevel === 'first_cycle' ? (subject.coefficient ?? '') : '',
      coefficientSecondCycle: variantLevel === 'second_cycle' ? (subject.coefficient ?? '') : ''
    });
    setEditDialogOpen(true);
  };

  const normalizeCoefficientValue = (value) => {
    if (value === null || value === undefined) return null;
    const trimmed = String(value).trim();
    if (!trimmed) return null;
    const parsed = Number(trimmed);
    if (!Number.isFinite(parsed)) return NaN;
    return parsed;
  };

  const handleSaveSubject = async () => {
    if (!editFormData.name || !editFormData.code) {
      enqueueSnackbar(t('manageSubjects.messages.fillRequiredFields'), { variant: 'error' });
      return;
    }

    const resolvedPhaseCode = (activePhase || localStorage.getItem('schoolAdmin_activePhase') || localStorage.getItem('activePhase') || '').toString().toLowerCase();
    const isSecPhase = resolvedPhaseCode === 'sec';

    const normalizedCoefficient = normalizeCoefficientValue(editFormData.coefficient);
    if (Number.isNaN(normalizedCoefficient)) {
      enqueueSnackbar(t('manageSubjects.messages.coefficientNumber'), { variant: 'error' });
      return;
    }
    if (normalizedCoefficient !== null && (normalizedCoefficient < 1 || normalizedCoefficient > 5)) {
      enqueueSnackbar(t('manageSubjects.messages.coefficientRange'), { variant: 'error' });
      return;
    }

    // For SEC, we only update the current variant's coefficient.
    // Secondary grouped subject validation: require the coefficient for the active variant.
    const variantLevel = (editingSubject?.level || editFormData.level || getSubjectDisplayLevel(editingSubject) || '').toString();
    if (isSecPhase && editingSubject?.subjectGroupId) {
      if (variantLevel === 'first_cycle') {
        if (!String(editFormData.coefficientFirstCycle || '').trim()) {
          enqueueSnackbar(t('manageSubjects.messages.coefficientNumber'), { variant: 'error' });
          return;
        }
      }
      if (variantLevel === 'second_cycle') {
        if (!String(editFormData.coefficientSecondCycle || '').trim()) {
          enqueueSnackbar(t('manageSubjects.messages.coefficientNumber'), { variant: 'error' });
          return;
        }
      }
    }

    try {
      setSaving(true);

      const updateData = {
        name: editFormData.name,
        code: editFormData.code,
        description: editFormData.description,
        categoryId: editFormData.categoryId || null,
        // Do not let grouped SEC variants change level from the edit dialog.
        // Level identifies the variant (first_cycle vs second_cycle). Changing it can make the row
        // disappear from the current phase/level filters, making it look like "save did nothing".
        level: (isSecPhase && editingSubject?.subjectGroupId) ? (variantLevel || null) : (editFormData.level || null),
        coefficient: normalizedCoefficient
      };

      if (isSecPhase && editingSubject?.subjectGroupId) {
        const c1 = normalizeCoefficientValue(editFormData.coefficientFirstCycle);
        const c2 = normalizeCoefficientValue(editFormData.coefficientSecondCycle);
        if (variantLevel === 'first_cycle' && !Number.isNaN(c1) && c1 !== null) updateData.coefficient = c1;
        if (variantLevel === 'second_cycle' && !Number.isNaN(c2) && c2 !== null) updateData.coefficient = c2;
      }

      // Build context headers locally (eslint fix: define headers in this scope)
      const schoolId = localStorage.getItem('schoolId') || localStorage.getItem('activeSchoolId') || localStorage.getItem('scopeSchoolId') || '';
      const phaseId = localStorage.getItem('schoolAdmin_activePhaseId') || localStorage.getItem('activePhaseId') || '';
      const phaseCode = (activePhase || localStorage.getItem('schoolAdmin_activePhase') || localStorage.getItem('activePhase') || '');
      const headers = schoolId ? { 'X-Scope-School-Id': schoolId, 'X-Active-Phase': phaseCode, 'X-Active-Phase-Id': phaseId, 'Content-Type': 'application/json' } : { 'X-Active-Phase': phaseCode, 'X-Active-Phase-Id': phaseId, 'Content-Type': 'application/json' };

              await fetchJson(`/api/subjects/${editingSubject.id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(updateData)
      });

      // Update local state
      const updatedSubjects = subjects.map(subj => 
        subj.id === editingSubject.id 
          ? { ...subj, ...updateData }
          : subj
      );
      setSubjects(updatedSubjects);

      enqueueSnackbar(t('manageSubjects.messages.subjectUpdated'), { variant: 'success', anchorOrigin: { vertical: 'top', horizontal: 'right' } });
      
      // Close dialog and reset form
      setEditDialogOpen(false);
      setEditingSubject(null);
      setEditFormData({ name: '', code: '', description: '', categoryId: '', coefficient: '', coefficientFirstCycle: '', coefficientSecondCycle: '' });
      
      // Refresh data after a short delay to reflect any server-side propagation (SEC groups)
      setTimeout(() => { fetchData(); }, 600);
      
    } catch (error) {
      console.error('Error updating subject:', error);
      enqueueSnackbar(t('manageSubjects.messages.subjectUpdateError', { message: error.message }), { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSubject = async (subject) => {
    if (window.confirm(t('manageSubjects.messages.confirmDelete', { name: subject.name }))) {
      try {
        const schoolId = localStorage.getItem('schoolId') || localStorage.getItem('activeSchoolId') || localStorage.getItem('scopeSchoolId') || '';
        const phaseId = localStorage.getItem('schoolAdmin_activePhaseId') || localStorage.getItem('activePhaseId') || '';
        const phaseCode = (activePhase || localStorage.getItem('schoolAdmin_activePhase') || localStorage.getItem('activePhase') || '');
        const headers = schoolId ? { 'X-Scope-School-Id': schoolId, 'X-Active-Phase': phaseCode, 'X-Active-Phase-Id': phaseId } : { 'X-Active-Phase': phaseCode, 'X-Active-Phase-Id': phaseId };
        await fetchJson(`/api/subjects/${subject.id}`, {
          method: 'DELETE',
          headers
        });

        // Remove from local state
        const updatedSubjects = subjects.filter(s => s.id !== subject.id);
        setSubjects(updatedSubjects);

        enqueueSnackbar(t('manageSubjects.messages.subjectDeleted'), { variant: 'success' });
        
      } catch (error) {
        console.error('Error deleting subject:', error);
        enqueueSnackbar(t('manageSubjects.messages.subjectDeleteError', { message: error.message }), { variant: 'error' });
      }
    }
  };

  const handleCancelEdit = () => {
    setEditDialogOpen(false);
    setEditingSubject(null);
    setEditFormData({ name: '', code: '', description: '', level: '', categoryId: '', coefficient: '', isActive: true });
  };

  const handleDeleteCategory = async (cat) => {
    if (!window.confirm(t('manageSubjects.messages.confirmDeleteCategory', { defaultValue: `Delete category "${cat.name}"? Subjects in this category will become uncategorized.`, name: cat.name }))) return;
    try {
      const schoolId = localStorage.getItem('schoolId') || localStorage.getItem('activeSchoolId') || localStorage.getItem('scopeSchoolId') || '';
      const phaseCode = (activePhase || localStorage.getItem('schoolAdmin_activePhase') || localStorage.getItem('activePhase') || '');
      const phaseId = localStorage.getItem('schoolAdmin_activePhaseId') || localStorage.getItem('activePhaseId') || '';
      const headers = {
        ...(schoolId ? { 'X-Scope-School-Id': schoolId } : {}),
        ...(phaseCode ? { 'X-Active-Phase': phaseCode } : {}),
        ...(phaseId ? { 'X-Active-Phase-Id': phaseId } : {})
      };
      await fetchJson(`/api/subject-categories/${cat.id}`, { method: 'DELETE', headers });
      setCategories(prev => prev.filter(c => c.id !== cat.id));
      enqueueSnackbar(t('manageSubjects.messages.categoryDeleted', { defaultValue: 'Category deleted' }), { variant: 'success' });
    } catch (err) {
      console.error('Error deleting category:', err);
      enqueueSnackbar(t('manageSubjects.messages.categoryDeleteError', { defaultValue: 'Failed to delete category', message: err.message }), { variant: 'error' });
    }
  };

  const getFilteredSubjects = () => {
    let list = subjects;
    if (levelFilter !== 'all') {
      list = list.filter((subject) => {
        const lvl = (subject?.level || '').toString().trim();
        if (!lvl) return true;
        return lvl === levelFilter;
      });
    }
    if (classFilter !== 'all') {
      list = list.filter((subject) => {
        const sid = String(subject?.id || '');
        const cls = subjectClassesMap[sid] || [];
        return (cls || []).some((c) => String(c?.id) === String(classFilter));
      });
    }
    if (!searchTerm) return list;
    
    return list.filter(subject => 
      subject.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      subject.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (subject.description && subject.description.toLowerCase().includes(searchTerm.toLowerCase()))
    );
  };

  const getCategoryName = (categoryId) => {
    const category = categories.find(c => c.id === categoryId);
    return category ? category.name : t('common.uncategorized');
  };

  // removed status logic (not in DB)

  if (loading) {
    return <AppLoader fullscreen message={t('manageSubjects.loadingSubjects')} />;
  }

  const filteredSubjects = getFilteredSubjects();

  const formatSubjectCoefficient = (subject) => {
    if (subject.coefficient === null || subject.coefficient === undefined || subject.coefficient === '') {
      if (resolvedPhaseCode === 'nur_pri') return t('manageSubjects.coefficient.hiddenForNursery');
      if (resolvedPhaseCode === 'sec') return t('manageSubjects.coefficient.firstCycleDefault');
      return '—';
    }
    return subject.coefficient;
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/subject-management')}
          sx={{ mr: 2 }}
        >
          {t('common.back')}
        </Button>
        <Box>
          <Typography variant="h4" component="h1">
            {t('manageSubjects.title')}
          </Typography>
          <Typography variant="body1" color="textSecondary">
            {t('manageSubjects.subtitle')}
          </Typography>
        </Box>
      </Box>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h4" color="primary">
                {subjects.length}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {t('manageSubjects.summary.totalSubjects')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        {/* Removed active/inactive summary cards (status not in DB) */}
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h4" color="info.main">
                {new Set(subjects.map(s => s.categoryId)).size}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {t('manageSubjects.summary.categoriesUsed')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile>
          <Tab icon={<CategoryIcon />} iconPosition="start" label={t('manageSubjects.tabs.categories', { defaultValue: 'Categories' }) + ` (${categories.length})`} />
          <Tab icon={<SubjectIcon />} iconPosition="start" label={t('manageSubjects.tabs.subjects', { defaultValue: 'Subjects' })} />
        </Tabs>
      </Paper>

      {/* === Subjects === */}
      {activeTab === 1 && <React.Fragment>
      {/* Search and Actions */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <SearchField
              fullWidth
              label={t('manageSubjects.search.label')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={t('manageSubjects.search.placeholder')}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>{t('subjectManagement.add.fields.level', { defaultValue: 'Level' })}</InputLabel>
              <Select
                value={levelFilter}
                label={t('subjectManagement.add.fields.level', { defaultValue: 'Level' })}
                onChange={(e) => setLevelFilter(String(e.target.value || 'all'))}
              >
                <MenuItem value="all">{t('common.all', { defaultValue: 'All' })}</MenuItem>
                <MenuItem value="nursery">{levelLabel('nursery')}</MenuItem>
                <MenuItem value="primary">{levelLabel('primary')}</MenuItem>
                <MenuItem value="first_cycle">{levelLabel('first_cycle')}</MenuItem>
                <MenuItem value="second_cycle">{levelLabel('second_cycle')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>{t('subjectReports.filters.class.label', { defaultValue: 'Class' })}</InputLabel>
              <Select
                value={classFilter}
                label={t('subjectReports.filters.class.label', { defaultValue: 'Class' })}
                onChange={(e) => setClassFilter(String(e.target.value || 'all'))}
              >
                <MenuItem value="all">{t('common.all', { defaultValue: 'All' })}</MenuItem>
                {(classes || []).map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.name}{c.section ? `-${c.section}` : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <Button
              startIcon={<AddIcon />}
              onClick={() => navigate('/subject-management/add-subject')}
              variant="contained"
              fullWidth
            >
              {t('subjectManagement.quickActions.addNewSubject.title')}
            </Button>
          </Grid>
          <Grid item xs={12} md={3}>
            <Button
              startIcon={<SearchIcon />}
              onClick={() => setSearchTerm('')}
              variant="outlined"
              fullWidth
            >
              {t('manageSubjects.actions.clearSearch')}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Subjects Table */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          {t('manageSubjects.table.title', { count: filteredSubjects.length })}
        </Typography>
        
        <TableContainer sx={{ overflowX: 'auto', width: '100%' }}>
          <Table sx={{ minWidth: 900 }}>
                         <TableHead>
               <TableRow>
                 <TableCell>{t('manageSubjects.table.headers.subject')}</TableCell>
                 <TableCell>{t('manageSubjects.table.headers.code')}</TableCell>
                 <TableCell>{t('subjectManagement.add.fields.level', { defaultValue: 'Level' })}</TableCell>
                 <TableCell>
                   {t('manageSubjects.table.headers.coefficient')}
                   <Typography variant="caption" display="block" color="textSecondary">
                     {t('manageSubjects.table.headers.coefficientCaption')}
                   </Typography>
                 </TableCell>
                 <TableCell>{t('manageSubjects.table.headers.category')}</TableCell>
                 <TableCell>{t('manageSubjects.table.headers.description')}</TableCell>
                 {/* Removed Status column */}
                 <TableCell>{t('common.actions')}</TableCell>
               </TableRow>
             </TableHead>
            <TableBody>
              {filteredSubjects.map((subject) => {
                const displayLevel = getSubjectDisplayLevel(subject);
                return (
                  <TableRow key={subject.id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <SubjectIcon sx={{ mr: 1, color: 'primary.main' }} />
                        {subject.name}
                      </Box>
                    </TableCell>
                                         <TableCell>
                       <Chip 
                         label={subject.code} 
                         variant="outlined" 
                         size="small" 
                       />
                     </TableCell>
                     <TableCell>
                       <Chip label={levelLabel(displayLevel)} size="small" variant="outlined" />
                     </TableCell>
                     <TableCell>
                       <Chip 
                         label={formatSubjectCoefficient(subject)} 
                         color="primary" 
                         size="small" 
                       />
                     </TableCell>
                     <TableCell>{getCategoryName(subject.categoryId)}</TableCell>
                    <TableCell>
                      {subject.description || t('classManagement.manage.na')}
                    </TableCell>
                    {/* Status column removed */}
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <IconButton
                          onClick={() => handleEditSubject(subject)}
                          color="primary"
                          size="small"
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          onClick={() => handleDeleteSubject(subject)}
                          color="error"
                          size="small"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {filteredSubjects.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="textSecondary">
              {searchTerm ? t('manageSubjects.empty.search') : t('manageSubjects.empty.default')}
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Edit Subject Dialog */}
      <Dialog open={editDialogOpen} onClose={handleCancelEdit} maxWidth="md" fullWidth>
        <DialogTitle>
          {t('manageSubjects.dialog.title')}
          {editingSubject && (
            <Typography variant="body2" color="textSecondary">
              {editingSubject.name} ({editingSubject.code})
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('manageSubjects.dialog.fields.name')}
                value={editFormData.name}
                onChange={(e) => setEditFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder={t('manageSubjects.dialog.placeholders.name')}
                required
              />
            </Grid>
                         <Grid item xs={12} md={6}>
               <TextField
                 fullWidth
                 label={t('manageSubjects.dialog.fields.code')}
                 value={editFormData.code}
                 onChange={(e) => setEditFormData(prev => ({ ...prev, code: e.target.value }))}
                 placeholder={t('manageSubjects.dialog.placeholders.code')}
                 required
               />
             </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth disabled={(() => {
                const resolvedPhaseCode = (activePhase || localStorage.getItem('schoolAdmin_activePhase') || localStorage.getItem('activePhase') || '').toString().toLowerCase();
                const isSec = resolvedPhaseCode === 'sec';
                const isGrouped = !!editingSubject?.subjectGroupId;
                return isSec && isGrouped;
              })()}>
                <InputLabel>{t('subjectManagement.add.fields.level', { defaultValue: 'Level' })}</InputLabel>
                <Select
                  value={editFormData.level}
                  label={t('subjectManagement.add.fields.level', { defaultValue: 'Level' })}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, level: String(e.target.value || '') }))}
                >
                  <MenuItem value=""><em>{t('common.na', { defaultValue: 'N/A' })}</em></MenuItem>
                  <MenuItem value="nursery">{t('subjectManagement.add.levels.nursery', { defaultValue: 'Nursery' })}</MenuItem>
                  <MenuItem value="primary">{t('subjectManagement.add.levels.primary', { defaultValue: 'Primary' })}</MenuItem>
                  <MenuItem value="first_cycle">{t('subjectManagement.add.levels.firstCycle', { defaultValue: 'First Cycle' })}</MenuItem>
                  <MenuItem value="second_cycle">{t('subjectManagement.add.levels.secondCycle', { defaultValue: 'Second Cycle' })}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
             
            <Grid item xs={12} md={6}>
              {(() => {
                const resolvedPhaseCode = (activePhase || localStorage.getItem('schoolAdmin_activePhase') || localStorage.getItem('activePhase') || '').toString().toLowerCase();
                const isSec = resolvedPhaseCode === 'sec';
                const isGrouped = !!editingSubject?.subjectGroupId;
                if (isSec && isGrouped) {
                  const variantLevel = (
                    editingSubject?.level ||
                    editFormData?.level ||
                    getSubjectDisplayLevel(editingSubject) ||
                    ''
                  ).toString();
                  const isSecond = variantLevel === 'second_cycle';
                  return (
                    <TextField
                      fullWidth
                      type="number"
                      label={isSecond
                        ? t('manageSubjects.dialog.fields.coefficientSecondCycle', { defaultValue: 'Second Cycle Coefficient' })
                        : t('manageSubjects.dialog.fields.coefficientFirstCycle', { defaultValue: 'First Cycle Coefficient' })
                      }
                      value={isSecond ? editFormData.coefficientSecondCycle : editFormData.coefficientFirstCycle}
                      onChange={(e) => setEditFormData(prev => (isSecond
                        ? ({ ...prev, coefficientSecondCycle: e.target.value })
                        : ({ ...prev, coefficientFirstCycle: e.target.value })
                      ))}
                      inputProps={{ min: 1, max: 5 }}
                    />
                  );
                }
                return (
                  <TextField
                    fullWidth
                    type="number"
                    label={t('manageSubjects.dialog.fields.coefficient')}
                    value={editFormData.coefficient}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, coefficient: e.target.value }))}
                    placeholder={t('manageSubjects.dialog.placeholders.coefficient')}
                    inputProps={{ min: 1, max: 5 }}
                  />
                );
              })()}
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>{t('manageSubjects.dialog.fields.category')}</InputLabel>
                <Select
                  value={editFormData.categoryId}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, categoryId: e.target.value }))}
                  label={t('manageSubjects.dialog.fields.category')}
                >
                  <MenuItem value="">
                    <em>{t('manageSubjects.dialog.placeholders.selectCategory')}</em>
                  </MenuItem>
                  {categories.map((category) => (
                    <MenuItem key={category.id} value={category.id}>
                      {category.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {/* Removed Active Subject switch (status not in DB) */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('manageSubjects.dialog.fields.description')}
                value={editFormData.description}
                onChange={(e) => setEditFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder={t('manageSubjects.dialog.placeholders.description')}
                multiline
                rows={3}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelEdit} disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleSaveSubject}
            variant="contained"
            startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
            disabled={saving}
          >
            {saving ? t('common.saving') : t('subjectAssignments.actions.saveAndClose')}
          </Button>
        </DialogActions>
      </Dialog>
      </React.Fragment>}

      {/* === TAB 1: Categories === */}
      {activeTab === 0 && <React.Fragment>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 1, mb: 2 }}>
          <Typography variant="h6">
            {t('manageSubjects.categories.title', { defaultValue: 'Subject Categories' })}
          </Typography>
          <Button
            startIcon={<AddIcon />}
            variant="contained"
            onClick={() => navigate('/subject-management/add-subject', { state: { activeTab: 0 } })}
          >
            {t('manageSubjects.categories.addCategory', { defaultValue: 'Add Category' })}
          </Button>
        </Box>

        <TableContainer sx={{ overflowX: 'auto', width: '100%' }}>
          <Table sx={{ minWidth: 560 }}>
            <TableHead>
              <TableRow>
                <TableCell>{t('manageSubjects.categories.headers.name', { defaultValue: 'Name' })}</TableCell>
                <TableCell>{t('subjectManagement.add.categories.fields.level', { defaultValue: 'Level' })}</TableCell>
                <TableCell>{t('manageSubjects.categories.headers.description', { defaultValue: 'Description' })}</TableCell>
                <TableCell>{t('manageSubjects.categories.headers.subjectsCount', { defaultValue: 'Subjects' })}</TableCell>
                <TableCell>{t('common.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {categories.map((cat) => {
                const subjectCount = subjects.filter(s => String(s.categoryId) === String(cat.id)).length;
                return (
                  <TableRow key={cat.id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <CategoryIcon sx={{ mr: 1, color: 'info.main' }} />
                        <Typography fontWeight="medium">{cat.name}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip label={levelLabel(getCategoryDisplayLevel(cat))} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      {cat.description || <Typography color="textSecondary" variant="body2">{t('classManagement.manage.na', { defaultValue: '—' })}</Typography>}
                    </TableCell>
                    <TableCell>
                      <Chip label={subjectCount} size="small" color={subjectCount > 0 ? 'primary' : 'default'} />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <IconButton
                          onClick={() => navigate('/subject-management/add-subject', { state: { activeTab: 0, editCategoryId: cat.id } })}
                          color="primary"
                          size="small"
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton onClick={() => handleDeleteCategory(cat)} color="error" size="small">
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {categories.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CategoryIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography variant="body1" color="textSecondary">
              {t('manageSubjects.categories.empty', { defaultValue: 'No categories found. Add one to organize your subjects.' })}
            </Typography>
          </Box>
        )}
      </Paper>
      </React.Fragment>}
    </Box>
  );
};

export default ManageSubjectsPage;
